import React from 'react'
import {
  As,
  DEFAULT_BREAKPOINT,
  HubResponsiveArray,
  HubResponsiveValue,
  HubStyleObject,
  StandardSizes,
  useBreakpointValue,
} from '@hub/design-system-base'
import { Box } from '@hub/box'
import { Frame, StandardRatios } from '@hub/frame'
import { Image as ChakraImage } from '@chakra-ui/image'
import {
  SGPageTemplateImageType,
  SGPageTemplateImage,
} from '../../../types/page-templates'
import { Video } from '../video'
import makeImageUrl from './helpers/make-image-url'
import findBestImage from './helpers/find-best-image'
import { asArray } from './helpers/maybe-array'

interface CloudinaryImageProps {
  ratio: HubResponsiveArray<StandardRatios>

  // pass multiple images to allow the component to choose the best fit
  imageSetOrImage: SGPageTemplateImage | SGPageTemplateImage[]

  // Choose an image type for a certain breakpoint e.g. ['SQUARE', null,
  // 'WIDE'] will use the 'SQUARE' image type for mobile and
  // 'MASTER' for desktop.
  //
  // If the preferred image type is not available for the breakpoint it will
  // fallback in the order of 'SQUARE', 'WIDE' you can control the fallback
  // order by passing an array for a breakpoint:
  //
  // e.g. [['SQUARE', null, 'WIDE'], null, ['WIDE', null, 'SQUARE']]
  //
  // This will try 'SQUARE', then 'WIDE' for mobile and 'WIDE', then 'SQAURE'
  // for desktop.
  //
  // If you want to set the fallback order for all breakpoints
  // you can pass just an array for the first breakpoint:
  //
  // e.g. [['WIDE', null, 'SQUARE']] this will try 'WIDE' then 'SQUARE' for all breakpoints
  preferredImageType?: HubResponsiveArray<
    SGPageTemplateImageType | SGPageTemplateImageType[]
  >

  // if the image needs to be made to fit the ratio it will either crop it or pad it
  // default is 'crop-face'
  // 'crop-face' - will crop the image to fit the ratio using Cloudinary's face detection to choose crop position
  // 'crop-center' - will crop the image to fit the ratio using Cloudinary centering the image
  // 'pad' will pad the image with empty space to fit the ratio
  resizeMode?: HubResponsiveArray<'crop-face' | 'crop-center' | 'pad'>

  as?: As
  sx?: HubStyleObject
  minWidth?: HubResponsiveValue<StandardSizes>
  maxWidth?: HubResponsiveValue<StandardSizes>

  imageRef?: any // React.ElementRef<typeof ChakraImage>
}

export function CloudinaryImage({
  ratio,
  imageSetOrImage,
  preferredImageType,
  resizeMode,
  maxWidth,
  minWidth,
  sx,
  as,
  imageRef,
}: CloudinaryImageProps): JSX.Element {
  const currentRatio = useBreakpointValue(asArray(ratio), DEFAULT_BREAKPOINT)
  const currentPrerredImageType = useBreakpointValue(
    asArray(preferredImageType),
    DEFAULT_BREAKPOINT
  )
  const currentResizeMode = useBreakpointValue(
    asArray(resizeMode),
    DEFAULT_BREAKPOINT
  )

  const preferredImage = findBestImage(imageSetOrImage, currentPrerredImageType)
  const url = makeImageUrl(preferredImage, currentRatio, currentResizeMode)

  return (
    <Frame
      ref={imageRef}
      as={as}
      ratio={ratio}
      minWidth={minWidth}
      maxWidth={maxWidth}
      sx={{ position: 'relative', isolation: 'isolate', ...sx }}
    >
      <>
        <ChakraImage
          src={url}
          alt={preferredImage.alt ?? undefined}
          boxSize="size-full"
        />
        {preferredImage.cinemagraphUrl && (
          <Box
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
            }}
          >
            <Video
              wide={ratio === '21/9'}
              url={preferredImage.cinemagraphUrl}
            />
          </Box>
        )}
      </>
    </Frame>
  )
}
